import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import Api from '../../../../../helpers/api';
import { sendRequest } from '../../../../../helpers/saga';
import { logger } from '../../../../../helpers/logger';
import notifications from '../../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../../helpers/core';
import { t } from '../../../../../i18n';

const api = new Api('workers/:worker_id/accreditations');

function formatDocumentImages(document_images) {
  return (
    document_images
      // eslint-disable-next-line no-underscore-dangle
      .filter(image => (image.document_image_url.startsWith('https://') ? image._destroy : true))
      .map(image => ({
        id: image.id,
        // eslint-disable-next-line no-underscore-dangle
        _destroy: image._destroy,
        // eslint-disable-next-line no-underscore-dangle
        document_image: image._destroy ? undefined : image.document_image_url,
      }))
  );
}

function* loadData({ payload }) {
  try {
    const { id, talentAccreditationId } = payload;

    const result = yield call(sendRequest, api.load, { id: [id, talentAccreditationId] }, notifications.REQUEST_ERROR);

    if (result?.data?.success?.data) {
      const { data } = result.data.success;

      let document_image_url = null;

      if (data.document_image_url) {
        const image = yield call(
          sendRequest,
          new Api(data.document_image_url).loadImage,
          {},
          notifications.REQUEST_ERROR,
        );

        if (image) {
          if (image.data) {
            document_image_url = image.data.fileBlobUrl;
          }
        }
      }

      const dataToSave = document_image_url ? { ...data, document_image_url } : data;

      yield put(actions.loadDataSuccess({ data: dataToSave }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const { id } = payload;

    const result = yield call(sendRequest, api.loadFormData, { id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      yield put(actions.loadFormData({ data: result.data.success.data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateAccreditation(data) {
  try {
    const { payload } = data;
    const { editForm, id, talentAccreditationId } = payload;
    const propsArray = Object.values(editForm);

    const accreditation_submission = keyByProperty(propsArray, 'name', prop => prop.value);

    const { accreditation, document_images, ...otherProps } = accreditation_submission;

    const result = yield call(
      sendRequest,
      api.updateItem,
      {
        data: {
          accreditation_submission: {
            ...otherProps,
            accreditation_id: accreditation,
            document_images_attributes: formatDocumentImages(document_images),
          },
        },
        id: [id, talentAccreditationId],
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        yield put(actions.saveSuccess({ data: accreditation_submission }));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', { item: t('dash.talent.accreditation_label') }),
          }),
        );
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.error_headline'),
            type: 'error',
            message: t('dash.common.notification.check_validation_errors'),
          }),
        );

        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addAccreditation(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;
    const propsArray = Object.values(editForm);

    const accreditation_submission = keyByProperty(propsArray, 'name', prop => prop.value);

    const { accreditation, document_images, ...otherProps } = accreditation_submission;

    const result = yield call(
      sendRequest,
      api.createItem,
      {
        data: {
          accreditation_submission: {
            ...otherProps,
            accreditation_id: accreditation,
            document_images_attributes: formatDocumentImages(document_images),
          },
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        yield put(actions.addSuccess({ data: accreditation_submission }));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.added_successfully', { item: t('dash.talent.accreditation_label') }),
          }),
        );
      } else {
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.LOAD, loadFormData),
    takeEvery(actions.CREATE, loadFormData),
    takeEvery(actions.SAVE, updateAccreditation),
    takeEvery(actions.ADD, addAccreditation),
  ]);
}
